import { useCallback, useState, useRef, FormEventHandler, useEffect } from 'react';
import classnames from 'classnames';
import { IHeaderParams } from 'ag-grid-community';
import { PinAshIcon } from '@glu/icons-react';
import { Button, EXTRA_SMALL, SECONDARY, SMALL, TERTIARY } from '@glu/buttons-react';
import { useClickOutside } from '@glu/utilities-react';
import locale from '@glu/locale';
import { getFilterComponent, IOnFilterChangeProps } from '@glu/data-components';
import { createAgGridFilter, getFilterable } from 'Filters/utils';
import useStyles from './Menu.styles';

interface IMenuProps extends IHeaderParams {
  closePopover(): void;
}

const Menu = ({ api, closePopover, column, columnApi, displayName }: IMenuProps) => {
  const classes = useStyles();
  const wrappingRef = useRef<HTMLDivElement>(null);
  const [localFilter, setLocalFilter] = useState(() => {
    const filterValues = api.getFilterModel();
    return filterValues[column.getColId()];
  });
  const columnDef = column.getColDef();
  const pinnedDirection = column.getPinned();
  const [pinChanged, setPinChanged] = useState(false);
  const [localPin, setLocalPin] = useState<'left' | 'right' | null>();

  const onApply = useCallback<FormEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const filterComponent = api.getFilterInstance(column.getColId());
      if (filterComponent) {
        filterComponent.setModel(localFilter || null);
        api.onFilterChanged();
      }
      if (pinChanged) {
        columnApi.setColumnPinned(column, localPin);
      }
      closePopover();
    },
    [api, closePopover, column, columnApi, localFilter, localPin, pinChanged]
  );

  const onClear = useCallback(() => {
    setLocalFilter(undefined);
    const filterComponent = api.getFilterInstance(column.getColId());
    if (filterComponent) {
      filterComponent.setModel(null);
      api.onFilterChanged();
    }
    closePopover();
  }, [api, closePopover, column]);

  const onFilterChange = useCallback((filterParams: IOnFilterChangeProps) => {
    const { filterable: filterableParam, value } = filterParams;
    const { componentType, filterOptions } = filterableParam;
    const { inRangeInclusive } = filterOptions || {};
    const newFilter = createAgGridFilter({ componentType, inRangeInclusive, value } as any);
    setLocalFilter(newFilter);
  }, []);

  useClickOutside(wrappingRef, closePopover);

  useEffect(() => {
    let isMounted = true;
    const setNewState = () => {
      if (!isMounted) return;
      const filterValues = api.getFilterModel();
      setLocalFilter(filterValues[column.getColId()]);
    };
    api.addEventListener('filterChanged', setNewState);
    return () => {
      isMounted = false;
      api.removeEventListener('filterChanged', setNewState);
    };
  }, [api, column]);

  return (
    <div ref={wrappingRef}>
      <form onSubmit={onApply}>
        {!columnDef.lockPinned && (
          <div
            className={classnames(classes.pinSection, {
              [classes.pinSectionNoBorder]: !columnDef.filter,
            })}
          >
            <PinAshIcon noIconWrapper />
            <p>{locale.get('gridReact.pinColumn')}</p>
            <div className={classes.pinButtons}>
              <Button
                className={classnames({
                  [classes.pinBold]: localPin === 'left',
                  [classes.pinHidden]: pinnedDirection === 'left',
                })}
                onClick={() => {
                  setPinChanged(true);
                  setLocalPin('left');
                }}
                size={SMALL}
                variant={TERTIARY}
              >
                {locale.get('gridReact.left')}
              </Button>
              <Button
                className={classnames({
                  [classes.pinBold]: localPin === 'right',
                  [classes.pinHidden]: pinnedDirection === 'right',
                  [classes.pinLast]: !pinnedDirection,
                })}
                onClick={() => {
                  setPinChanged(true);
                  setLocalPin('right');
                }}
                size={SMALL}
                variant={TERTIARY}
              >
                {locale.get('gridReact.right')}
              </Button>
              <Button
                className={classnames({
                  [classes.pinBold]: localPin === null,
                  [classes.pinHidden]: !pinnedDirection,
                  [classes.pinLast]: pinnedDirection,
                })}
                onClick={() => {
                  setPinChanged(true);
                  setLocalPin(null);
                }}
                size={SMALL}
                variant={TERTIARY}
              >
                {locale.get('gridReact.removePin')}
              </Button>
            </div>
          </div>
        )}
        {columnDef.filter && (
          <div className={classes.filterWrapper}>
            <p className={classes.filterLabel}>{locale.get('gridReact.filter')}</p>
            {getFilterComponent({
              filterable: getFilterable({ api, column, columnDef, displayName }),
              filterValue: localFilter,
              onFilterChange,
            })}
          </div>
        )}
        <div>
          <Button size={EXTRA_SMALL} type="submit" variant={SECONDARY}>
            {locale.get('gridReact.apply')}
          </Button>
          <Button className={classes.clearButton} onClick={onClear} size={SMALL} variant={TERTIARY}>
            {locale.get('gridReact.clear')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Menu;
